import { useState, useEffect } from 'react';

import { FaTrash } from 'react-icons/fa';
import styled from 'styled-components';

import { DetailLink } from 'components/DetailsView';
import { TotemImage } from 'components/TotemImage';
import { TotemInput } from 'components/TotemInput';
import { TotemLabel } from 'components/TotemLabel';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemSelect } from 'components/TotemSelect';

import { GetDetailedProductQuery, GetProductsWithRecipesQuery, HotDrinksIngredientUnit } from 'data/__generated__';

export type Ingredient = {
    productId: string;
    quantity: number;
    hotDrinksIngredientUnit: HotDrinksIngredientUnit;
};

export type RecipeFormData = {
    selectedProductId: string | null;
    ingredients: Ingredient[];
};

type ProductWithRecipe = NonNullable<GetDetailedProductQuery['detailedProduct']>;
type ProductFromList = GetProductsWithRecipesQuery['getAllProducts'][number];

export const HotDrinksRecipeDetailsContent = ({
    product,
    availableIngredients,
    productsWithoutRecipes,
    isCreating,
    onFormChange,
}: {
    product: ProductWithRecipe | null;
    availableIngredients: ProductFromList[];
    productsWithoutRecipes: ProductFromList[];
    isCreating: boolean;
    onFormChange: (data: RecipeFormData) => void;
}) => {
    const [selectedProductId, setSelectedProductId] = useState<string | null>(product?._id || null);
    const [ingredients, setIngredients] = useState<Ingredient[]>(
        product?.recipe?.ingredients.map((ing) => ({
            productId: ing.productId,
            quantity: ing.quantity,
            hotDrinksIngredientUnit: ing.hotDrinksIngredientUnit,
        })) || [],
    );

    useEffect(() => {
        onFormChange({ selectedProductId, ingredients });
    }, [selectedProductId, ingredients, onFormChange]);

    function addIngredient(productId: string) {
        if (!ingredients.some((ing) => ing.productId === productId)) {
            setIngredients([
                ...ingredients,
                {
                    productId,
                    quantity: 1,
                    hotDrinksIngredientUnit: HotDrinksIngredientUnit.G,
                },
            ]);
        }
    }

    function removeIngredient(productId: string) {
        setIngredients(ingredients.filter((ing) => ing.productId !== productId));
    }

    function updateIngredientQuantity(productId: string, quantity: number) {
        setIngredients(ingredients.map((ing) => (ing.productId === productId ? { ...ing, quantity } : ing)));
    }

    function updateIngredientUnit(productId: string, hotDrinksIngredientUnit: HotDrinksIngredientUnit) {
        setIngredients(
            ingredients.map((ing) => (ing.productId === productId ? { ...ing, hotDrinksIngredientUnit } : ing)),
        );
    }

    const productsOptions = productsWithoutRecipes
        .map(({ _id, fullname }) => ({
            value: _id,
            label: fullname,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const availableIngredientsOptions = availableIngredients
        .filter(({ _id }) => !ingredients.some((ing) => ing.productId === _id))
        .map(({ _id, fullname }) => ({
            value: _id,
            label: fullname,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const unitOptions = [{ value: HotDrinksIngredientUnit.G, label: 'g' }];

    const getIngredientProduct = (productId: string) => availableIngredients.find(({ _id }) => _id === productId);

    return (
        <Content>
            <ScrollableContent>
                {isCreating && (
                    <SelectContainer>
                        <TotemSelect
                            label="Produit boisson chaude"
                            placeholder="Sélectionner le produit pour cette recette"
                            value={selectedProductId}
                            options={productsOptions}
                            onChange={(value) => {
                                setSelectedProductId(value);
                            }}
                        />
                    </SelectContainer>
                )}

                {!isCreating && product && (
                    <ProductInfo>
                        <TotemLabel>Produit</TotemLabel>
                        <ProductDisplay>
                            <TotemImage src={product.imageUrl} alt={product.fullname || ''} size={50} />
                            <DetailLink name={product.fullname || product.name} value={product._id} path="product" />
                        </ProductDisplay>
                    </ProductInfo>
                )}

                <SelectContainer>
                    <TotemSelect
                        label="Ajouter un ingrédient"
                        placeholder="Sélectionner un ingrédient (chocolat, sucre, gobelet...)"
                        value={null}
                        options={availableIngredientsOptions}
                        onChange={(value) => {
                            if (value) {
                                addIngredient(value);
                            }
                        }}
                    />
                </SelectContainer>

                <TotemLabel>Ingrédients de la recette</TotemLabel>
                {ingredients.length === 0 ? (
                    <EmptyMessage>Aucun ingrédient ajouté</EmptyMessage>
                ) : (
                    <IngredientsTable>
                        <thead>
                            <tr>
                                <TableHeaderCell />
                                <TableHeaderCell>Composant</TableHeaderCell>
                                <TableHeaderCell>Quantité par paquet (g)</TableHeaderCell>
                                <TableHeaderCell>Unité</TableHeaderCell>
                                <TableHeaderCell>Quantité</TableHeaderCell>
                                <TableHeaderCell />
                            </tr>
                        </thead>
                        <tbody>
                            {ingredients.map((ingredient) => {
                                const { productId, quantity, hotDrinksIngredientUnit } = ingredient;
                                const ingredientProduct = getIngredientProduct(productId);
                                if (!ingredientProduct) {
                                    return null;
                                }
                                const fullname = ingredientProduct.fullname || '';
                                const imageUrl = ingredientProduct.imageUrl;

                                const weightWithoutPackaging = ingredientProduct.weightWithoutPackaging;

                                const ingredientWeightInGrams =
                                    weightWithoutPackaging != null ? weightWithoutPackaging * 1000 : null;

                                return (
                                    <tr key={productId}>
                                        <TableBodyCell>
                                            <TotemImage src={imageUrl} alt={fullname} size={50} />
                                        </TableBodyCell>
                                        <TableBodyCell>
                                            <DetailLink name={fullname} value={productId} path="product" />
                                        </TableBodyCell>
                                        <TableBodyCell>{ingredientWeightInGrams ?? '-'}</TableBodyCell>
                                        <TableBodyCell>
                                            <UnitSelectContainer>
                                                <TotemSelect
                                                    placeholder="-"
                                                    value={hotDrinksIngredientUnit ?? null}
                                                    options={unitOptions}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            updateIngredientUnit(productId, value);
                                                        }
                                                    }}
                                                />
                                            </UnitSelectContainer>
                                        </TableBodyCell>
                                        <TableBodyCell>
                                            <QuantityInput
                                                type="number"
                                                value={quantity}
                                                min={0.01}
                                                step={0.01}
                                                onChange={(value) =>
                                                    updateIngredientQuantity(productId, parseFloat(value) || 0)
                                                }
                                            />
                                        </TableBodyCell>
                                        <TableBodyCell $isDataCentered>
                                            <TotemPrimaryButton
                                                type="button"
                                                onClick={() => removeIngredient(productId)}
                                            >
                                                <FaTrash size={20} />
                                            </TotemPrimaryButton>
                                        </TableBodyCell>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </IngredientsTable>
                )}
            </ScrollableContent>
        </Content>
    );
};

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SelectContainer = styled.div``;

const ProductInfo = styled.div``;

const ProductDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 5px;
`;

const EmptyMessage = styled.p`
    color: ${({ theme }) => theme.textColor};
    font-style: italic;
`;

const IngredientsTable = styled.table`
    width: 100%;
    margin-top: 10px;
    border-radius: ${({ theme }) => theme.borderRadius};
    border-spacing: 0px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const TableHeaderCell = styled.th<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
`;

const TableBodyCell = styled.td<{ $isDataCentered?: boolean }>`
    padding: 10px;
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
`;

const QuantityInput = styled(TotemInput)`
    width: 100px;
`;

const UnitSelectContainer = styled.div`
    width: 100px;
`;
